import React from 'react';
import AlertIcon from '@uber/base-icons/alert';
import ChevronDown from '@uber/base-icons/chevron-down';
import type { Theme } from 'baseui';
import { useStyletron } from 'baseui';
import { KIND as NOTIFICATION_KIND, Notification } from 'baseui/notification';
import { Radio } from 'baseui/radio';
import type { OnChangeParams } from 'baseui/select';
import { Select, SIZE } from 'baseui/select';
import { LabelXSmall, ParagraphXSmall } from 'baseui/typography';
import { useAtomValue } from 'jotai';
import { multiVideoListAtom, selectedVideoIndexAtom } from '../../../labelling-v2/atoms/multi-video-atoms';
import { useMediaState } from '../../providers/hooks/use-media-state';
import { useMediaStateLiteOnPlayStatusChange, useMediaStateLiteResetMediaState } from '../../providers/hooks/use-media-state-lite';
import useAnalyticsForVideoLabelling from '../frames-control-panel/analytics/useAnalyticsForVideoLabelling';
export type VideoItem = {
  url: string;
  label: string;
  referenceUri: string;
};
type VideoViewSelectorProps = {
  onSelectVideo: (index: number) => void;
};
const RadioOverrides = {
  Root: {
    style: {
      margin: 0
    }
  }
};
const getValueLabel = ({
  option
}: {
  option: {
    label: string;
  };
}) => <LabelXSmall margin={0}>{option.label}</LabelXSmall>;
const SelectOverrides = {
  Root: {
    style: {
      maxWidth: '150px'
    }
  },
  ControlContainer: {
    style: ({
      $theme
    }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: $theme.sizing.scale300
    })
  },
  Dropdown: {
    style: ({
      $theme
    }) => ({
      maxHeight: $theme.sizing.scale4800,
      width: '150px'
    })
  },
  SelectArrow: {
    component: () => <ChevronDown size={12} />
  },
  DropdownListItem: {
    style: {
      paddingTop: 0,
      paddingBottom: 0
    }
  }
};
const NotificationOverrides = {
  Body: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      marginTop: 0,
      marginBottom: 0,
      marginRight: 0,
      width: '100%',
      borderRadius: $theme.borders.radius400,
      paddingTop: 0,
      paddingBottom: 0
    })
  },
  InnerContainer: {
    style: ({
      $theme
    }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: $theme.sizing.scale300
    })
  }
};
export const VideoViewSelector = ({
  onSelectVideo
}: VideoViewSelectorProps) => {
  const [css, theme] = useStyletron();
  const {
    sendEventChangeVideo
  } = useAnalyticsForVideoLabelling();
  const videos = useAtomValue(multiVideoListAtom);
  const selectedIndex = useAtomValue(selectedVideoIndexAtom);
  const {
    onPlayStatusChange
  } = useMediaState();
  const onPlayStatusChangeLite = useMediaStateLiteOnPlayStatusChange();
  const resetMediaStateLite = useMediaStateLiteResetMediaState();
  const isMainViewSelected = selectedIndex === 0;
  const options = videos.map((video, index) => ({
    id: index,
    label: video.label
  }));
  const selectedOption = options[selectedIndex] ? [options[selectedIndex]] : [];
  const handleChange = (params: OnChangeParams) => {
    const selected = params.value[0];
    if (selected) {
      onSelectVideo(Number(selected.id));
      sendEventChangeVideo({
        videoTitle: (selected.label as string),
        selectedIndex: Number(selected.id)
      });
    }
    onPlayStatusChange(false);
    onPlayStatusChangeLite(false);
    resetMediaStateLite();
  };
  const getOptionLabel = ({
    option
  }: any) => {
    const isSelected = option.id === selectedIndex;
    return <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: theme.sizing.scale300,
      height: theme.sizing.scale900
    })}>
        <Radio value={String(option.id)} checked={isSelected} overrides={{
        ...RadioOverrides,
        Label: () => <ParagraphXSmall margin={0}>{option.label}</ParagraphXSmall>
      }}></Radio>
        <ParagraphXSmall>{option.label}</ParagraphXSmall>
      </div>;
  };
  if (videos.length <= 1) {
    return null;
  }
  return <div data-testid="video-view-selector-container" className={css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.sizing.scale400,
    padding: `${theme.sizing.scale300} ${theme.sizing.scale600}`,
    backgroundColor: theme.colors.backgroundPrimary,
    borderBottom: `1px solid ${theme.colors.borderOpaque}`,
    paddingRight: '0'
  })}>
      <div data-testid="video-view-selector-dropdown" className={css({
      maxWidth: theme.sizing.scale4800
    })}>
        <Select options={options} labelKey="label" valueKey="id" size={SIZE.default} value={selectedOption} onChange={handleChange} clearable={false} searchable={false} placeholder="Select view" getOptionLabel={getOptionLabel} getValueLabel={getValueLabel} overrides={{
        ...SelectOverrides
      }} />
      </div>

      {!isMainViewSelected && <Notification kind={NOTIFICATION_KIND.warning} overrides={{
      ...NotificationOverrides
    }}>
          <AlertIcon size={20} />
          <ParagraphXSmall>
            You cannot create labels in this view. Switch to main view to continue labelling
          </ParagraphXSmall>
        </Notification>}
    </div>;
};